import type { ReactNode } from "react";
import { cn } from "../cn";
import { useI18n } from "../../i18n";

/**
 * PublicPageShell — MACRO #26 · Increment 3.
 *
 * Shared editorial layout for the four PUBLIC information pages (About /
 * How It Works / Explore / Creators & Brands). It lives INSIDE the scroll
 * AppShell variant, so the fixed neon backdrop + shared top navigation are
 * already provided by the shell — this component only owns the page's own
 * hero + content rhythm. It NEVER touches the frozen Home (Overview) body.
 */
export function PublicPageShell({
  testId,
  eyebrow,
  index,
  title,
  intro,
  children,
  footer,
}: {
  testId: string;
  eyebrow: string;
  index: string;
  title: ReactNode;
  intro?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div
      data-testid={testId}
      className="relative w-full min-h-full px-4 md:px-8 lg:px-12 py-10 md:py-16"
    >
      <div className="max-w-[1200px] mx-auto">
        {/* Hero */}
        <header className="max-w-[820px]">
          <div className="flex items-center gap-3">
            <span aria-hidden className="h-2 w-2 rotate-45 bg-neon-teal shadow-glow-teal-sm" />
            <span className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-neon-teal">
              {index}
            </span>
            <span aria-hidden className="h-3 w-px bg-border-divider" />
            <span className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-text-secondary">
              {eyebrow}
            </span>
          </div>
          <h1
            data-testid={`${testId}-title`}
            className={cn(
              "mt-6 font-hero uppercase text-text-primary",
              "text-[11vw] md:text-[clamp(2.5rem,min(6.4vw,9vh),96px)]",
              "leading-[0.92] tracking-[0.01em]",
            )}
          >
            {title}
          </h1>
          {intro && (
            <p className="mt-6 max-w-[640px] font-display text-[15px] leading-[1.65] text-text-secondary">
              {intro}
            </p>
          )}
        </header>

        {/* Body */}
        <div className="mt-12 md:mt-16">{children}</div>

        {footer && <div className="mt-16 md:mt-20">{footer}</div>}
      </div>
    </div>
  );
}

/** Shared closing CTA used by the static public pages. */
export function PublicPageCta() {
  const { t } = useI18n();
  return (
    <div
      data-testid="public-page-cta"
      className="neon-glass p-7 md:p-10 flex flex-col gap-5 md:flex-row md:items-center md:justify-between"
    >
      <div className="max-w-[560px]">
        <div className="font-hero uppercase text-[24px] md:text-[30px] leading-tight text-text-primary">
          {t("shellCta.title")}
        </div>
        <p className="mt-2 font-display text-[13.5px] leading-[1.6] text-text-secondary">
          {t("shellCta.body")}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 shrink-0">
        <a
          data-testid="public-cta-explore"
          href="#explore"
          className="facet-btn h-10 px-5 no-underline text-[11px] font-semibold tracking-[0.2em] justify-center"
        >
          {t("shellCta.explore")}
        </a>
        <a
          data-testid="public-cta-territory"
          href="#grid"
          className={cn(
            "inline-flex items-center justify-center gap-2 h-10 px-5",
            "font-display text-[11px] font-semibold tracking-[0.2em] uppercase",
            "text-text-primary no-underline pill-gradient-border",
          )}
        >
          {t("shellCta.enterGrid")}
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
            <path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      </div>
    </div>
  );
}
